// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sct569n3y2.execute-api.us-east-1.amazonaws.com/prod';

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Centralized error handling function
export const handleApiError = (error: unknown): string => {
  if (error instanceof ApiError) {
    // Handle server errors (5xx)
    if (error.status >= 500) {
      return 'Uh oh, something broke! Please try again. If this keeps up please reach out to the groom.';
    }
    
    // Handle client errors (4xx) - return the actual error message
    return error.message;
  }
  
  // Handle network or other errors
  return 'Uh oh, something broke! Please try again. If this keeps up please reach out to the groom.';
};

// Types
export interface AuthResponse {
  authenticated: boolean;
  token?: string;
  userType?: string;
  guestId?: string;
  message?: string;
}

export interface ContactInfo {
  streetAddress?: string;
  apt?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  email?: string;
  mobile?: string;
}

export interface Guest {
  id: string;
  fullName: string;
  searchableNames: string[];
  pseudonyms?: string[];
  attending: boolean;
  hasResponded: boolean;
  rsvp?: RSVPResponse | null;
  contactInfo?: ContactInfo;
  inviteToken?: string;
}

export interface GuestSearchResponse {
  found: boolean;
  guest: Guest | null;
  message: string;
}

export interface PartyMember {
  name: string;
  attending: boolean;
}

export type RSVPEventId = 'welcomeDrinks' | 'rehearsalDinner' | 'ceremony' | 'poolParty';

export interface EventRSVP {
  partyMembers: PartyMember[];
}

export type EventResponses = Record<RSVPEventId, EventRSVP>;

export interface RSVPData {
  guestId: string;
  attendance: 'yes' | 'no';
  partyMembers?: PartyMember[];
  eventResponses?: EventResponses;
  message?: string;
}

export interface RSVPResponse {
  id: string;
  guestId: string;
  attendance: 'yes' | 'no';
  partyMembers: PartyMember[];
  eventResponses?: EventResponses;
  submittedAt: string;
}

interface RSVPSubmitResponse {
  success: boolean;
  message: string;
  rsvp: RSVPResponse;
}

export interface AdminStats {
  totalParties: number;
  totalGuests: number;
  respondedParties: number;
  attendingParties: number;
  attendingGuests: number;
  responseRate: number;
}

export interface CreateGuestData {
  partyName: string;
  searchableNames: string[];
  pseudonyms?: string[];
}

export interface UpdateGuestData {
  partyName?: string;
  searchableNames?: string[];
  pseudonyms?: string[];
}

export interface AddressInfo {
  id: string;  // Changed from inviteId to id to match API
  partyName: string;  // Changed from inviteNames to partyName
  streetAddress: string;
  apt?: string;
  country: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  mobile: string;
}

// For GET response - direct flat structure
export interface AddressInfoResponse extends AddressInfo {
  // The API returns the flat structure directly
}

export interface AddressSubmitResponse {
  success: boolean;
  data: AddressInfo;
  message: string;
}

// Storage utility for better iOS compatibility
class AuthStorage {
  private static getStorageKey(): string {
    return 'wedding_auth_token';
  }

  static setToken(token: string): void {
    try {
      // Try localStorage first
      localStorage.setItem(this.getStorageKey(), token);
      
      // Also try sessionStorage as fallback for iOS
      sessionStorage.setItem(this.getStorageKey(), token);
      
      // Store in a cookie as additional fallback for iOS Safari
      document.cookie = `${this.getStorageKey()}=${token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`;
    } catch (error) {
      console.warn('Failed to store auth token:', error);
    }
  }

  static getToken(): string | null {
    try {
      // Try localStorage first
      let token = localStorage.getItem(this.getStorageKey());
      if (token) return token;

      // Try sessionStorage
      token = sessionStorage.getItem(this.getStorageKey());
      if (token) {
        // Restore to localStorage if found in sessionStorage
        this.setToken(token);
        return token;
      }

      // Try cookie as last resort
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === this.getStorageKey()) {
          if (value) {
            // Restore to localStorage if found in cookie
            this.setToken(value);
            return value;
          }
        }
      }
    } catch (error) {
      console.warn('Failed to retrieve auth token:', error);
    }
    
    return null;
  }

  static removeToken(): void {
    try {
      localStorage.removeItem(this.getStorageKey());
      sessionStorage.removeItem(this.getStorageKey());
      // Clear cookie
      document.cookie = `${this.getStorageKey()}=; path=/; max-age=0`;
    } catch (error) {
      console.warn('Failed to remove auth token:', error);
    }
  }
}

// API Client Class
class ApiClient {
  private baseURL: string;
  private authToken: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    // Try to restore auth token using our robust storage system
    this.authToken = AuthStorage.getToken();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(errorData.message || `HTTP ${response.status}: ${response.statusText}`, response.status, response.statusText);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Authentication
  async checkAuth(): Promise<AuthResponse> {
    // Ensure we have the latest token from storage
    if (!this.authToken) {
      this.authToken = AuthStorage.getToken();
    }
    return this.request<AuthResponse>('/auth');
  }

  refreshTokenFromStorage(): void {
    this.authToken = AuthStorage.getToken();
  }

  async authenticate(password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });

    if (response.token) {
      this.authToken = response.token;
      AuthStorage.setToken(response.token);
    }

    return response;
  }

  logout(): void {
    this.authToken = null;
    AuthStorage.removeToken();
  }

  // Guests
  async searchGuest(guestName: string): Promise<GuestSearchResponse> {
    const params = new URLSearchParams({ guestName });
    return this.request<GuestSearchResponse>(`/guest?${params.toString()}`);
  }

  async getGuest(id: string): Promise<GuestSearchResponse> {
    // For individual guests, the guest handler will return their own data
    // based on the JWT token, regardless of the ID parameter
    return this.request<GuestSearchResponse>('/guest');
  }

  // RSVP
  async submitRSVP(data: RSVPData): Promise<RSVPResponse> {
    const response = await this.request<RSVPSubmitResponse>('/rsvp', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.rsvp;
  }

  async updateRSVP(_id: string, data: Partial<RSVPData>): Promise<RSVPResponse> {
    // Use the same POST endpoint for updates; backend handles create/update logic
    const response = await this.request<RSVPSubmitResponse>('/rsvp', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.rsvp;
  }

  // Admin endpoints
  async getAdminStats(): Promise<AdminStats> {
    return this.request<AdminStats>('/admin/stats');
  }

  async getAllGuests(): Promise<Guest[]> {
    const response = await this.request<{ guests: Guest[] }>('/admin/guests');
    return response.guests;
  }

  async getAllGuestsWithContactInfo(): Promise<Guest[]> {
    const response = await this.request<{ guests: Guest[] }>('/admin/guests?includeContactInfo=true');
    return response.guests;
  }

  async createGuest(data: CreateGuestData): Promise<Guest> {
    const response = await this.request<{ guest: Guest }>('/admin/guests', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.guest;
  }

  async updateGuest(id: string, data: UpdateGuestData): Promise<Guest> {
    const response = await this.request<{ guest: Guest }>(`/admin/guests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return response.guest;
  }

  async deleteGuest(id: string): Promise<void> {
    await this.request<{ success: boolean }>(`/admin/guests/${id}`, {
      method: 'DELETE'
    });
  }

  // Address Info endpoints - using pre-signed token from URL
  async getAddressInfo(token: string): Promise<AddressInfoResponse> {
    return this.request<AddressInfoResponse>(`/addrInfo?token=${encodeURIComponent(token)}`, {
      method: 'GET'
    });
  }

  async submitAddressInfo(token: string, data: AddressInfo): Promise<AddressSubmitResponse> {
    return this.request<AddressSubmitResponse>('/addrInfo', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        token: token  // Include the token in the body as 'token'
      })
    });
  }
}

// Export singleton instance - always use real API
export const apiClient = new ApiClient(API_BASE_URL);